import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, FormField } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, Package, ShoppingCart, RotateCcw, RefreshCw,
  Save, MessageSquare, User, ShieldCheck, Info, Upload, X,
} from "lucide-react";

const VOLUMETRIC_DIVISOR = 5000;
const MIN_DEAD_WEIGHT = 0.5;

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const emptyReplacement = () => ({ name: "", sku: "", unit_price: "", qty: 1 });

const B2CCreateReturn = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [orders, setOrders] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  const [orderId, setOrderId] = useState("");
  const [type, setType] = useState("return"); // return | exchange
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]); // [{name, sku, qty, max, checked}]
  const [pkg, setPkg] = useState({ dead_weight_kg: "", length_cm: "", breadth_cm: "", height_cm: "" });
  const [replacements, setReplacements] = useState([emptyReplacement()]);
  const [qcRequired, setQcRequired] = useState(false);
  const [qcAttrs, setQcAttrs] = useState({ brand: "", color: "", serial_no: "", size: "", ean_barcode: "", image_url: "" });
  const [qcImageUploading, setQcImageUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onQcImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPEG, PNG or WEBP)");
      return;
    }
    setQcImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/b2c/seller/returns/qc-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setQcAttrs((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(errDetail(err, "Image upload failed"));
    } finally {
      setQcImageUploading(false);
    }
  };

  useEffect(() => {
    setLoadingOrders(true);
    axios
      .get(`${API}/b2c/seller/orders`, { params: { status_group: "delivered", page_size: 200 } })
      .then((r) => setOrders(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch((e) => {
        if (e?.response?.status !== 422) toast.error(errDetail(e, "Failed to load delivered orders"));
      })
      .finally(() => setLoadingOrders(false));
    axios
      .get(`${API}/b2c/seller/returns/reasons`)
      .then((r) => setReasons(Array.isArray(r.data?.reasons) ? r.data.reasons : []))
      .catch(() => {});
  }, []);

  const selectedOrder = useMemo(() => orders.find((o) => o.id === orderId) || null, [orders, orderId]);

  // When an order is chosen, prefill items + package from it.
  const applyOrder = useCallback((order) => {
    if (!order) {
      setItems([]);
      return;
    }
    setItems(
      (order.products || []).map((p) => ({
        name: p.name, sku: p.sku || "", qty: p.qty || 1, max: p.qty || 1, checked: true,
      }))
    );
    const pk = order.package || {};
    setPkg({
      dead_weight_kg: String(pk.dead_weight_kg ?? "0.5"),
      length_cm: String(pk.length_cm ?? ""),
      breadth_cm: String(pk.breadth_cm ?? ""),
      height_cm: String(pk.height_cm ?? ""),
    });
  }, []);

  useEffect(() => {
    applyOrder(selectedOrder);
  }, [selectedOrder, applyOrder]);

  // Deep-link: ?order_id=... preselects the order.
  useEffect(() => {
    const oid = searchParams.get("order_id");
    if (oid) setOrderId(oid);
  }, []);

  const weights = useMemo(() => {
    const dead = parseFloat(pkg.dead_weight_kg) || 0;
    const l = parseFloat(pkg.length_cm) || 0;
    const b = parseFloat(pkg.breadth_cm) || 0;
    const h = parseFloat(pkg.height_cm) || 0;
    const volumetric = (l * b * h) / VOLUMETRIC_DIVISOR;
    return { volumetric: round2(volumetric), applicable: round2(Math.max(dead, volumetric, MIN_DEAD_WEIGHT)) };
  }, [pkg]);

  const setItem = (i, field, value) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));

  const updateReplacement = (i, field, value) =>
    setReplacements((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  const addReplacement = () => setReplacements((prev) => [...prev, emptyReplacement()]);
  const removeReplacement = (i) =>
    setReplacements((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const validate = () => {
    if (!orderId) return "Select the delivered order to return";
    if (!reason) return "Choose a reason";
    const chosen = items.filter((it) => it.checked && parseInt(it.qty) > 0);
    if (chosen.length === 0) return "Select at least one item to return";
    if ((parseFloat(pkg.dead_weight_kg) || 0) < MIN_DEAD_WEIGHT) return `Dead weight must be at least ${MIN_DEAD_WEIGHT} kg`;
    if (!(parseFloat(pkg.length_cm) > 0 && parseFloat(pkg.breadth_cm) > 0 && parseFloat(pkg.height_cm) > 0))
      return "Enter package length, breadth and height";
    if (type === "exchange") {
      const valid = replacements.filter((p) => p.name.trim() && parseFloat(p.unit_price) > 0 && parseInt(p.qty) > 0);
      if (valid.length === 0) return "Add at least one replacement product for the exchange";
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        order_id: orderId,
        type,
        reason,
        reason_note: note.trim() || null,
        items: items
          .filter((it) => it.checked && parseInt(it.qty) > 0)
          .map((it) => ({ name: it.name, sku: it.sku || null, qty: parseInt(it.qty) })),
        package: {
          dead_weight_kg: parseFloat(pkg.dead_weight_kg),
          length_cm: parseFloat(pkg.length_cm),
          breadth_cm: parseFloat(pkg.breadth_cm),
          height_cm: parseFloat(pkg.height_cm),
        },
        qc: qcRequired
          ? {
              required: true,
              brand: qcAttrs.brand.trim() || null,
              color: qcAttrs.color.trim() || null,
              serial_no: qcAttrs.serial_no.trim() || null,
              size: qcAttrs.size.trim() || null,
              ean_barcode: qcAttrs.ean_barcode.trim() || null,
              image_url: qcAttrs.image_url.trim() || null,
            }
          : { required: false },
        replacement_products:
          type === "exchange"
            ? replacements
                .filter((p) => p.name.trim() && parseFloat(p.unit_price) > 0 && parseInt(p.qty) > 0)
                .map((p) => ({
                  name: p.name.trim(),
                  sku: p.sku || null,
                  unit_price: parseFloat(p.unit_price),
                  qty: parseInt(p.qty),
                }))
            : null,
      };
      const { data } = await axios.post(`${API}/b2c/seller/returns`, payload);
      toast.success(`${type === "exchange" ? "Exchange" : "Return"} ${data.return_number} created — choose a courier to ship.`);
      navigate(`/seller/b2c/returns/${data.id}`);
    } catch (e) {
      toast.error(errDetail(e, "Failed to create the return"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <PageHeader
          title="New Return / Exchange"
          subtitle="Raise a reverse pickup against a delivered order"
          backTo={() => navigate("/seller/b2c/returns")}
        />

        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-6">
          {/* ① Order */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-brand" /> Delivered order
              </CardTitle>
              <CardDescription>Only delivered orders can be returned.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Order" htmlFor="return-order" required>
                {loadingOrders ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading delivered orders…
                  </div>
                ) : orders.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                    No delivered orders to return yet.
                  </div>
                ) : (
                  <SearchableSelect
                    value={orderId}
                    onValueChange={setOrderId}
                    placeholder="Select a delivered order"
                    emptyText="No matching orders."
                    options={orders.map((o) => ({
                      value: o.id,
                      label: `${o.order_number} · ${o.buyer?.name || ""}`,
                      searchLabel: `${o.order_number} ${o.buyer?.name || ""} ${o.buyer?.phone || ""} ${o.awb || ""}`,
                    }))}
                  />
                )}
              </FormField>

              {selectedOrder && (
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-700">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="font-medium">{selectedOrder.buyer?.name}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{selectedOrder.buyer?.phone}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {[selectedOrder.buyer?.address, selectedOrder.buyer?.city, selectedOrder.buyer?.pincode]
                      .filter(Boolean).join(", ")}
                    {selectedOrder.awb ? ` · AWB ${selectedOrder.awb}` : ""}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ② Type */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-brand" /> Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { id: "return", title: "Return", desc: "Pick the parcel back up — refund handled separately.", icon: RotateCcw },
                  { id: "exchange", title: "Exchange", desc: "Pick up the old item and ship a replacement.", icon: RefreshCw },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setType(opt.id)}
                    className={`flex items-start gap-3 rounded-lg border-2 px-5 py-4 text-left transition-all ${
                      type === opt.id ? "border-brand bg-brand-bg text-brand" : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <opt.icon className="w-5 h-5 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold text-sm">{opt.title}</div>
                      <div className="text-sm opacity-70">{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ③ Items to return */}
          {selectedOrder && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-brand" /> Items to return
                </CardTitle>
                <CardDescription>Pick the items being picked up and the quantity.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {items.map((it, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
                    <Checkbox checked={it.checked} onCheckedChange={(v) => setItem(i, "checked", !!v)} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{it.name}</p>
                      <p className="text-xs text-slate-500">{it.sku ? `SKU ${it.sku} · ` : ""}Ordered {it.max}</p>
                    </div>
                    <div className="w-24">
                      <Input
                        type="number" min="1" max={it.max} inputMode="numeric"
                        value={it.qty} disabled={!it.checked}
                        onChange={(e) => setItem(i, "qty", Math.min(it.max, Math.max(1, parseInt(e.target.value) || 1)))}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ④ Reason */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-brand" /> Reason
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Reason" htmlFor="return-reason" required>
                <SearchableSelect
                  value={reason}
                  onValueChange={setReason}
                  placeholder="Select a reason"
                  emptyText="No reasons."
                  options={reasons.map((r) => ({ value: r.code, label: r.label }))}
                />
              </FormField>
              <FormField label="Note (optional)" htmlFor="return-note">
                <Input id="return-note" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything the pickup agent should know" />
              </FormField>
            </CardContent>
          </Card>

          {/* ⑤ Pickup quality check (QC) */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-brand" /> Pickup quality check
              </CardTitle>
              <CardDescription>Have the pickup agent verify the item at the buyer's door before accepting the return.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { id: false, title: "Standard pickup", desc: "Pick up the parcel as-is (Non-QC)." },
                  { id: true, title: "Quality check (QC)", desc: "Agent verifies the item at the doorstep before accepting." },
                ].map((opt) => (
                  <button
                    key={String(opt.id)}
                    type="button"
                    onClick={() => setQcRequired(opt.id)}
                    data-testid={`b2c-qc-${opt.id ? "on" : "off"}`}
                    className={`flex items-start gap-3 rounded-lg border-2 px-5 py-4 text-left transition-all ${
                      qcRequired === opt.id ? "border-brand bg-brand-bg text-brand" : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold text-sm">{opt.title}</div>
                      <div className="text-sm opacity-70">{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>

              {qcRequired && (
                <>
                  <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
                    <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                    <span>QC is a doorstep check — the pickup agent verifies the item before accepting the return. It's honoured on supported couriers; on others your choice is recorded but may not be enforced. Add any reference details for the agent to verify — all optional (the product name is used automatically).</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      ["brand", "Brand"],
                      ["color", "Colour"],
                      ["serial_no", "Serial no."],
                      ["size", "Size"],
                      ["ean_barcode", "EAN / barcode"],
                    ].map(([k, label]) => (
                      <FormField key={k} label={label} htmlFor={`qc-${k}`}>
                        <Input id={`qc-${k}`} value={qcAttrs[k]} onChange={(e) => setQcAttrs({ ...qcAttrs, [k]: e.target.value })} />
                      </FormField>
                    ))}
                  </div>

                  {/* Reference image: upload an image OR paste a URL. */}
                  <FormField label="Reference image" htmlFor="qc-image-url" hint="Upload an image or paste a URL — the agent matches the returned item against it.">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <input id="qc-image-file" type="file" accept="image/*" className="hidden" onChange={onQcImageFile} />
                      <Button type="button" variant="outline" size="sm" disabled={qcImageUploading}
                        onClick={() => document.getElementById("qc-image-file").click()} data-testid="qc-image-upload">
                        {qcImageUploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                        Upload image
                      </Button>
                      <span className="text-xs text-slate-400 px-1">or</span>
                      <Input id="qc-image-url" className="flex-1" value={qcAttrs.image_url}
                        onChange={(e) => setQcAttrs({ ...qcAttrs, image_url: e.target.value })}
                        placeholder="Paste an image URL" />
                    </div>
                    {qcAttrs.image_url && (
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <a href={qcAttrs.image_url} target="_blank" rel="noreferrer" className="text-brand hover:underline truncate max-w-[280px]">{qcAttrs.image_url}</a>
                        <button type="button" className="inline-flex items-center text-red-500 hover:underline" onClick={() => setQcAttrs({ ...qcAttrs, image_url: "" })}>
                          <X className="w-3 h-3 mr-0.5" /> Remove
                        </button>
                      </div>
                    )}
                  </FormField>
                </>
              )}
            </CardContent>
          </Card>

          {/* ⑥ Replacement products (exchange only) */}
          {type === "exchange" && (
            <Card className="border-0 shadow-sm border-l-4 border-l-indigo-300">
              <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between space-y-0">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-indigo-500" /> Replacement to ship
                  </CardTitle>
                  <CardDescription>The new items shipped back to your buyer.</CardDescription>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addReplacement}>
                  <Plus className="w-4 h-4 mr-1" /> Add item
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {replacements.map((p, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-700">Replacement {i + 1}</span>
                      {replacements.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="text-red-500 hover:bg-red-50"
                          onClick={() => removeReplacement(i)} aria-label="Remove">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <FormField label="Product name" htmlFor={`r-name-${i}`} required>
                        <Input id={`r-name-${i}`} value={p.name} onChange={(e) => updateReplacement(i, "name", e.target.value)} />
                      </FormField>
                      <FormField label="SKU" htmlFor={`r-sku-${i}`}>
                        <Input id={`r-sku-${i}`} value={p.sku} onChange={(e) => updateReplacement(i, "sku", e.target.value)} />
                      </FormField>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Unit price (₹)" htmlFor={`r-price-${i}`} required>
                        <Input id={`r-price-${i}`} type="number" step="0.01" min="0" inputMode="decimal"
                          value={p.unit_price} onChange={(e) => updateReplacement(i, "unit_price", e.target.value)} />
                      </FormField>
                      <FormField label="Qty" htmlFor={`r-qty-${i}`} required>
                        <Input id={`r-qty-${i}`} type="number" min="1" inputMode="numeric"
                          value={p.qty} onChange={(e) => updateReplacement(i, "qty", e.target.value)} />
                      </FormField>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ⑥ Package */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-brand" /> Return package
              </CardTitle>
              <CardDescription>Defaults to the original parcel — adjust if the return is packed differently.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <FormField label="Dead weight (kg)" htmlFor="rpkg-dead" required hint={`Min ${MIN_DEAD_WEIGHT} kg`}>
                  <Input id="rpkg-dead" type="number" step="0.01" min={MIN_DEAD_WEIGHT} inputMode="decimal"
                    value={pkg.dead_weight_kg} onChange={(e) => setPkg({ ...pkg, dead_weight_kg: e.target.value })} />
                </FormField>
                <FormField label="Length (cm)" htmlFor="rpkg-l" required>
                  <Input id="rpkg-l" type="number" step="0.1" min="0" inputMode="decimal"
                    value={pkg.length_cm} onChange={(e) => setPkg({ ...pkg, length_cm: e.target.value })} />
                </FormField>
                <FormField label="Breadth (cm)" htmlFor="rpkg-b" required>
                  <Input id="rpkg-b" type="number" step="0.1" min="0" inputMode="decimal"
                    value={pkg.breadth_cm} onChange={(e) => setPkg({ ...pkg, breadth_cm: e.target.value })} />
                </FormField>
                <FormField label="Height (cm)" htmlFor="rpkg-h" required>
                  <Input id="rpkg-h" type="number" step="0.1" min="0" inputMode="decimal"
                    value={pkg.height_cm} onChange={(e) => setPkg({ ...pkg, height_cm: e.target.value })} />
                </FormField>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="border-slate-200">Volumetric {weights.volumetric} kg</Badge>
                <Badge className="bg-brand-bg text-brand border-0">Charged {weights.applicable} kg</Badge>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate("/seller/b2c/returns")} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="b2c-create-return">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Create {type === "exchange" ? "exchange" : "return"}
            </Button>
          </div>
        </form>
      </div>
    </SellerLayout>
  );
};

export default B2CCreateReturn;
